import { graphql } from "babel-plugin-relay/macro";
import { useFragment, useMutation } from "react-relay";
import { useGenerateContentSubmission_NodeFragment$key } from "@generated/useGenerateContentSubmission_NodeFragment.graphql";
import { useGenerateContentSubmission_StartContentNodeMutation } from "@generated/useGenerateContentSubmission_StartContentNodeMutation.graphql";
import { useGenerateContentSubmission_StartTreeMutation } from "@generated/useGenerateContentSubmission_StartTreeMutation.graphql";

const LEARN_OPPORTUNITY_FRAGMENT = graphql`
	fragment useGenerateContentSubmission_NodeFragment on LearnOpportunityV2 {
		id
		root {
			id
			structureDefinition {
				... on LearnOpportunityRootStructureDefinition {
					viewerTreeState {
						kind
						... on CanNotBeStartedViewerTreeState {
							reasonConfigTypes
						}
						... on IsStartedViewerTreeState {
							headContentId
						}
					}
				}
			}
		}
		typeDefinition {
			... on LearnOpportunityELearningContentTypeDefinition {
				containedElementTypes
				contentNodeAdvancementResult {
					status
				}
			}
			... on LearnOpportunityBranchTypeDefinition {
				children {
					id
					typeDefinition {
						... on LearnOpportunityELearningContentTypeDefinition {
							containedElementTypes
							contentNodeAdvancementResult {
								status
							}
						}
					}
				}
			}
			definitionType
		}
	}
`;
const START_TREE_MUTATION = graphql`
	mutation useGenerateContentSubmission_StartTreeMutation($input: StartTreeInput!) {
		LearnV2 {
			startTree(input: $input) {
				learnOpportunity {
					id
					structureDefinition {
						... on LearnOpportunityRootStructureDefinition {
							viewerTreeState {
								kind
								... on IsStartedViewerTreeState {
									headContentId
								}
							}
						}
					}
					typeDefinition {
						... on LearnOpportunityELearningContentTypeDefinition {
							contentNodeAdvancementResult {
								status
							}
						}
					}
				}
			}
		}
	}
`;

const START_CONTENT_NODE_MUTATION = graphql`
	mutation useGenerateContentSubmission_StartContentNodeMutation($input: StartContentNodeInput!) {
		LearnV2 {
			startContentNode(input: $input) {
				contentSubmission {
					id
				}
			}
		}
	}
`;
interface OwnProps {
	learnOpportunity: useGenerateContentSubmission_NodeFragment$key;
	loadQuery: any; //eslint-disable-line @typescript-eslint/no-explicit-any
	setIsReady: (v: boolean) => void;
	nodeId: string;
}
export function useGenerateContentSubmission({
	learnOpportunity,
	loadQuery,
	setIsReady,
	nodeId,
}: OwnProps) {
	const nodeFragment = useFragment<useGenerateContentSubmission_NodeFragment$key>(
		LEARN_OPPORTUNITY_FRAGMENT,
		learnOpportunity,
	)!;
	const [startTree] =
		useMutation<useGenerateContentSubmission_StartTreeMutation>(START_TREE_MUTATION);
	const [startContentNode] = useMutation<useGenerateContentSubmission_StartContentNodeMutation>(
		START_CONTENT_NODE_MUTATION,
	);
	return () => {
		const firstChild = nodeFragment.typeDefinition.children
			? nodeFragment.typeDefinition.children?.[0]
			: nodeFragment;
		const advanceContentStatus = firstChild?.typeDefinition.contentNodeAdvancementResult?.status;

		if (advanceContentStatus == "BlockedByTree") {
			const viewerTreeState = nodeFragment?.root?.structureDefinition.viewerTreeState?.kind;

			switch (viewerTreeState) {
				case "CanBeStarted": {
					const startAtContentNodeId =
						nodeFragment?.typeDefinition?.definitionType === "content" ? firstChild?.id : null;

					startTree({
						variables: {
							input: {
								nodeId: firstChild?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
								startAtContentNodeId,
							},
						},
						onCompleted: (startTreeResponse) => {
							const contentStatus =
								startTreeResponse.LearnV2.startTree?.learnOpportunity.typeDefinition
									?.contentNodeAdvancementResult?.status;

							if (contentStatus === "CanBeStarted") {
								// We also need to start the content node
								startContentNode({
									variables: {
										input: { contentNodeId: firstChild?.id! }, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
									},
									onCompleted() {
										loadQuery(
											{ learnOpportunityId: nodeId! as string },
											{ fetchPolicy: "network-only" },
										);
										///Do something here
										setIsReady(true);
									},
								});
								return;
							}
						},
					});
					break;
				}
				default:
					setIsReady(true);
			}
		} else {
			switch (advanceContentStatus) {
				case "CanBeStarted": {
					startContentNode({
						variables: { input: { contentNodeId: firstChild?.id! } }, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
						onCompleted() {
							loadQuery({ learnOpportunityId: nodeId! as string }, { fetchPolicy: "network-only" });
							setIsReady(true);
						},
					});
					break;
				}
				case "Continue": {
					setIsReady(true);
					break;
				}
				default:
					setIsReady(true);
			}
		}
	};
}
